/**
 * Halachic Calendar Utilities
 *
 * Pure functions for Hebrew date computation and calendar grid generation.
 * Uses @hebcal/core (HDate, Zmanim, Location only) for accurate halachic dates.
 *
 * Authority: apps/ystnj/documentation/prd/parent-portal/parnes-hayom/v1.1/implementation-plan.md
 *
 * Shared by both frontend (React hooks) and backend (Lambda handlers).
 */

import { HDate, Location, Zmanim } from '@hebcal/core';
import type {
  CalendarAvailability,
  CalendarDayEntry,
  CampaignCalendarGrid,
  GeoConfig,
  HebrewDateResult,
  SunsetResult,
} from './calendarTypes';

// ---------------------------------------------------------------------------
// Hebrew Date Computation
// ---------------------------------------------------------------------------

/**
 * Compute the Hebrew date for a given Gregorian date.
 *
 * When `sunsetApplied` is true, the Hebrew date is computed as if the date
 * has rolled forward (i.e. after sunset, the halachic day advances).
 */
export const computeHebrewDate = (
  gregorianDate: string | Date,
  sunsetApplied: boolean,
  _geo?: GeoConfig
): HebrewDateResult => {
  const jsDate = typeof gregorianDate === 'string' ? new Date(gregorianDate) : gregorianDate;

  // If sunset is applied, the halachic day is one day ahead
  const effectiveDate = sunsetApplied
    ? new Date(jsDate.getFullYear(), jsDate.getMonth(), jsDate.getDate() + 1)
    : jsDate;

  const hDate = new HDate(effectiveDate);

  const hebrewYear = hDate.getFullYear();
  const hebrewMonth = hDate.getMonth();
  const hebrewDay = hDate.getDate();

  return {
    hebrewYear,
    hebrewMonth,
    hebrewDay,
    hebrewDateLabel: hDate.renderGematriya(true),
    hebrewDateEnglish: hDate.render('en'),
    sunsetApplied,
    gregorianDate: formatIsoDate(jsDate),
  };
};

// ---------------------------------------------------------------------------
// Sunset Computation
// ---------------------------------------------------------------------------

/**
 * Compute sunset UTC for a given date and geographic location.
 */
export const computeSunsetUtc = (
  gregorianDate: string | Date,
  geo: GeoConfig
): SunsetResult => {
  const jsDate = typeof gregorianDate === 'string' ? new Date(gregorianDate) : gregorianDate;

  const location = new Location(
    geo.latitude,
    geo.longitude,
    false, // not Israel (configurable per tenant)
    geo.timezone,
    undefined,
    undefined,
    undefined,
    geo.elevationMeters ?? 0
  );

  const zmanim = new Zmanim(location, jsDate, Boolean(geo.elevationMeters));
  const sunset = zmanim.sunset();

  const sunsetUtc = sunset ? sunset.toISOString() : '';
  const isAfterSunset = sunset ? Date.now() > sunset.getTime() : false;

  return {
    sunsetUtc,
    isAfterSunset,
  };
};

// ---------------------------------------------------------------------------
// Calendar Grid Builder
// ---------------------------------------------------------------------------

/**
 * Build a full month calendar grid for campaign display.
 *
 * @param year - Gregorian year
 * @param month - Gregorian month (1-based; 1 = January)
 * @param sponsoredDates - Set of ISO date strings that are already sponsored
 * @param blackoutDates - Set of ISO date strings that are blacked out
 * @param campaignRange - Optional [startDate, endDate] ISO strings for the campaign window
 * @param geo - Geographic config for Hebrew date computation
 */
export const buildCalendarMonthGrid = (
  year: number,
  month: number,
  sponsoredDates: ReadonlySet<string>,
  blackoutDates: ReadonlySet<string>,
  campaignRange: readonly [string, string] | null,
  geo?: GeoConfig
): CampaignCalendarGrid => {
  // Month is 1-based; JS Date uses 0-based
  const firstDay = new Date(year, month - 1, 1);
  const daysInMonth = new Date(year, month, 0).getDate();
  const startDayOfWeek = firstDay.getDay(); // 0 = Sunday

  const today = new Date();
  const todayIso = formatIsoDate(today);

  const campaignStart = campaignRange ? campaignRange[0] : null;
  const campaignEnd = campaignRange ? campaignRange[1] : null;

  const monthLabel = firstDay.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const days: Array<CalendarDayEntry | null> = [];
  let availableCount = 0;
  let sponsoredCount = 0;

  // Pad leading nulls for days before the 1st
  for (let i = 0; i < startDayOfWeek; i++) {
    days.push(null);
  }

  for (let dayNum = 1; dayNum <= daysInMonth; dayNum++) {
    const date = new Date(year, month - 1, dayNum);
    const isoDate = formatIsoDate(date);
    const isToday = isoDate === todayIso;
    const hebrewDate = computeHebrewDate(isoDate, false, geo);

    const availability = resolveAvailability(
      isoDate,
      isToday,
      todayIso,
      sponsoredDates,
      blackoutDates,
      campaignStart,
      campaignEnd
    );

    if (availability === 'available') availableCount++;
    if (availability === 'sponsored') sponsoredCount++;

    days.push({
      gregorianDate: isoDate,
      hebrewDate,
      availability,
      dayOfMonth: dayNum,
      isToday,
    });
  }

  return {
    year,
    month,
    monthLabel,
    days,
    availableCount,
    sponsoredCount,
  };
};

// ---------------------------------------------------------------------------
// Formatting Helpers
// ---------------------------------------------------------------------------

/**
 * Format a Hebrew date result into a display label.
 */
export const formatHebrewDateLabel = (result: HebrewDateResult): string =>
  result.hebrewDateLabel || result.hebrewDateEnglish;

/**
 * Format a Date to ISO date string (YYYY-MM-DD).
 */
const formatIsoDate = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// ---------------------------------------------------------------------------
// Internal Helpers
// ---------------------------------------------------------------------------

const resolveAvailability = (
  isoDate: string,
  _isToday: boolean,
  todayIso: string,
  sponsoredDates: ReadonlySet<string>,
  blackoutDates: ReadonlySet<string>,
  campaignStart: string | null,
  campaignEnd: string | null
): CalendarAvailability => {
  // Past dates
  if (isoDate < todayIso) {
    return 'past';
  }

  // Out of campaign range
  if (campaignStart && isoDate < campaignStart) {
    return 'out-of-range';
  }
  if (campaignEnd && isoDate > campaignEnd) {
    return 'out-of-range';
  }

  // Blackout dates
  if (blackoutDates.has(isoDate)) {
    return 'blackout';
  }

  // Already sponsored
  if (sponsoredDates.has(isoDate)) {
    return 'sponsored';
  }

  return 'available';
};
